import { Dialog as Base } from "@base-ui/react/dialog";
import type { ComponentProps, ReactNode } from "react";

import "./surface.css";
import "./dialog.css";
import { cn } from "./cn";

interface DialogContentProps {
  children: ReactNode;
  className?: string;
}

/** Modal popup: scrim backdrop + centered panel. Focus is trapped and restored, Escape and
 * backdrop-click dismiss, and aria-labelledby/-describedby are wired from Title/Description —
 * all from Base UI's headless Dialog. */
function DialogContent({ children, className }: DialogContentProps) {
  return (
    <Base.Portal>
      <Base.Backdrop className="rc-dialog-backdrop" />
      <Base.Popup className={cn("rc-surface", "rc-dialog", className)}>{children}</Base.Popup>
    </Base.Portal>
  );
}

function DialogTitle({
  className,
  ...rest
}: Omit<ComponentProps<typeof Base.Title>, "className"> & { className?: string }) {
  return <Base.Title className={cn("rc-dialog__title", className)} {...rest} />;
}

function DialogDescription({
  className,
  ...rest
}: Omit<ComponentProps<typeof Base.Description>, "className"> & { className?: string }) {
  return <Base.Description className={cn("rc-dialog__description", className)} {...rest} />;
}

/**
 * Token-styled modal Dialog on Base UI. Compound API:
 * `<Dialog.Root><Dialog.Trigger render={<Button/>} /><Dialog.Content><Dialog.Title/>…<Dialog.Close/></Dialog.Content></Dialog.Root>`.
 */
export const Dialog = {
  Root: Base.Root,
  Trigger: Base.Trigger,
  Close: Base.Close,
  Content: DialogContent,
  Title: DialogTitle,
  Description: DialogDescription,
};
